"use client";

import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./ExportModal.module.css";

/**
 * MetadataPanel — Phase 25-AB Task 12 (panel 21-M · NEW · Task 18 #6 missing component).
 *
 * Mockup source: docs/mockups/export.html lines 625-640.
 * Captures the upload metadata that ships alongside the rendered video:
 * title (60), description (300), hashtags (10 max), category (auto-detected).
 *
 * Used by the ExportModal but also reusable from the editor's right rail
 * once integration lands.
 */

export interface MetadataValue {
	title: string;
	description: string;
	hashtags: string[];
	category: string;
}

export interface MetadataPanelProps {
	value: MetadataValue;
	onChange: (v: MetadataValue) => void;
	onAutoGenerate?: () => void;
}

export const CATEGORY_OPTIONS = [
	"People & Blogs",
	"Vlog",
	"Education",
	"Entertainment",
	"Music",
	"Gaming",
	"Sports",
	"News",
	"Tutorial",
	"Review",
] as const;

const TITLE_MAX = 60;
const DESC_MAX = 300;
const HASHTAG_MAX = 10;

export function MetadataPanel({
	value,
	onChange,
	onAutoGenerate,
}: MetadataPanelProps) {
	function update<K extends keyof MetadataValue>(
		key: K,
		next: MetadataValue[K],
	) {
		onChange({ ...value, [key]: next });
	}

	function handleHashtags(raw: string) {
		const tags = raw
			.split(/[\s,]+/)
			.map((t) => t.trim())
			.filter(Boolean)
			.map((t) => (t.startsWith("#") ? t : `#${t}`))
			.slice(0, HASHTAG_MAX);
		update("hashtags", tags);
	}

	const hashtagText = value.hashtags.join(" ");

	return (
		<section
			className={styles.rightBlock}
			data-testid="metadata-panel"
			aria-label="메타데이터"
			{...devLabelPanel("21-M")}
		>
			<div className={styles.rightTitle}>메타데이터 (제목·설명·해시태그)</div>

			<div className={styles.mpRow} {...devLabel("21-M1")}>
				<label className={styles.mpLabel} htmlFor="ex-meta-title">
					제목 ({TITLE_MAX}자)
				</label>
				<input
					id="ex-meta-title"
					className={styles.mpInput}
					maxLength={TITLE_MAX}
					value={value.title}
					onChange={(e) => update("title", e.target.value)}
					placeholder="이거 모르면 후회 — 후킹 제목을 입력하세요"
					data-testid="metadata-title"
				/>
			</div>

			<div className={styles.mpRow} {...devLabel("21-M2")}>
				<label className={styles.mpLabel} htmlFor="ex-meta-desc">
					설명 ({DESC_MAX}자)
				</label>
				<textarea
					id="ex-meta-desc"
					className={styles.mpInput}
					maxLength={DESC_MAX}
					rows={2}
					style={{ resize: "vertical", minHeight: 42 }}
					value={value.description}
					onChange={(e) => update("description", e.target.value)}
					placeholder="영상 설명을 입력하세요"
					data-testid="metadata-description"
				/>
			</div>

			<div className={styles.mpRow} {...devLabel("21-M3")}>
				<label className={styles.mpLabel} htmlFor="ex-meta-tags">
					해시태그 {HASHTAG_MAX} (쉼표·공백 구분)
				</label>
				<input
					id="ex-meta-tags"
					className={`${styles.mpInput} ${styles.mpMono}`}
					value={hashtagText}
					onChange={(e) => handleHashtags(e.target.value)}
					placeholder="#shorts #자동편집 #AI"
					data-testid="metadata-hashtags"
				/>
				<div className={styles.mpCount}>
					{value.hashtags.length} / {HASHTAG_MAX}
				</div>
			</div>

			<div className={styles.mpRow} {...devLabel("21-M4")}>
				<label className={styles.mpLabel} htmlFor="ex-meta-cat">
					카테고리
				</label>
				<select
					id="ex-meta-cat"
					className={styles.mpInput}
					value={value.category}
					onChange={(e) => update("category", e.target.value)}
					data-testid="metadata-category"
				>
					<option value="">선택...</option>
					{CATEGORY_OPTIONS.map((c) => (
						<option key={c} value={c}>
							{c}
						</option>
					))}
				</select>
			</div>

			{onAutoGenerate && (
				<button
					type="button"
					className={styles.mpAutoBtn}
					onClick={onAutoGenerate}
					data-testid="metadata-auto"
				>
					AI 자동 생성 (+1코인)
				</button>
			)}
		</section>
	);
}
